import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, scrollToId } from "@/data/content";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    setTimeout(() => scrollToId(href), open ? 350 : 0);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay: 1.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-background/80 backdrop-blur-xl border-b border-royal-line"
            : "bg-transparent border-b border-transparent"
        }`}
        data-testid="navbar"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
          <button
            onClick={() => go("#topo")}
            className="flex items-center"
            data-testid="nav-logo"
            aria-label="Voltar ao topo"
          >
            <img src="/assets/logo-blue.png" alt="Creative" className="h-9 w-auto" />
          </button>

          <nav className="hidden items-center gap-9 lg:flex" data-testid="nav-links">
            {NAV_LINKS.map((l) => (
              <button
                key={l.href}
                onClick={() => go(l.href)}
                className="group relative text-sm font-semibold text-foreground/70 transition-colors duration-300 hover:text-royal"
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-royal transition-all duration-500 group-hover:w-full" />
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={() => go("#contacto")}
              className="group hidden items-center gap-2 rounded-full bg-royal px-6 py-2.5 text-sm font-bold text-white transition-all duration-500 hover:bg-black/10 hover:shadow-[0_8px_32px_rgba(42,80,170,0.35)] lg:flex"
              data-testid="nav-cta-button"
            >
              Vamos criar
              <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:rotate-45" />
            </button>
            <button
              onClick={() => setOpen(true)}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-royal-line text-royal lg:hidden"
              data-testid="nav-menu-open"
              aria-label="Abrir menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[60] flex flex-col bg-royal"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 py-4">
              <img src="/assets/logo-white.png" alt="Creative" className="h-9 w-auto" />
              <button
                onClick={() => setOpen(false)}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/30 text-white"
                data-testid="nav-menu-close"
                aria-label="Fechar menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
              {[...NAV_LINKS, { label: "Vamos criar", href: "#contacto" }].map((l, i) => (
                <motion.button
                  key={l.href + l.label}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + i * 0.07, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  onClick={() => go(l.href)}
                  className="border-b border-white/15 py-5 text-left font-display text-4xl font-medium text-white transition-colors hover:text-white/70"
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {l.label}
                </motion.button>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
